<template>
    <div class="w-full max-w-2xl">
        <div class="bg-base-100 shadow-xl rounded-xl p-4">
            <div
                v-if="!entries.length"
                class="text-base-content/50 py-4"
            >
                No entries for this day.
            </div>

            <div
                v-else-if="compact"
                class="grid grid-cols-3 gap-2"
            >
                <div
                    v-for="entry in entries"
                    :key="entry.id"
                    class="relative rounded-lg overflow-hidden aspect-square cursor-pointer"
                    @click="selectedEntry = entry"
                >
                    <img
                        :src="entry.image_url || '/placeholder.png'"
                        :alt="entry.name"
                        class="w-full h-full object-cover"
                    />
                    <div class="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex flex-col justify-end p-1.5">
                        <span class="text-white text-xs font-medium leading-tight truncate">{{ entry.name }}</span>
                        <span
                            v-if="entry.calories"
                            class="text-white/80 text-xs"
                            >{{ entry.calories }} kcal</span
                        >
                    </div>
                    <div
                        v-if="entry.state === 'processing'"
                        class="absolute inset-0 bg-black/40 flex items-center justify-center"
                    >
                        <span class="loading loading-spinner loading-sm text-white"></span>
                    </div>
                </div>
            </div>

            <div
                v-else
                class="flex flex-col gap-3"
            >
                <div
                    v-for="entry in entries"
                    :key="entry.id"
                    class="flex gap-3 rounded-xl border border-base-200 p-3 cursor-pointer hover:bg-base-200/50 transition-colors"
                    @click="selectedEntry = entry"
                >
                    <div class="relative w-16 h-16 shrink-0 rounded-lg overflow-hidden">
                        <img
                            :src="entry.image_url || '/placeholder.png'"
                            :alt="entry.name"
                            class="w-full h-full object-cover"
                        />
                        <div
                            v-if="entry.state === 'processing'"
                            class="absolute inset-0 bg-black/40 flex items-center justify-center"
                        >
                            <span class="loading loading-spinner loading-xs text-white"></span>
                        </div>
                    </div>
                    <div class="flex flex-col justify-center min-w-0">
                        <span class="font-medium truncate">{{ entry.name || 'Processing...' }}</span>
                        <span class="text-base-content/60 text-sm truncate">{{ entry.description }}</span>
                        <div class="flex gap-3 mt-1 text-sm">
                            <span
                                v-if="entry.calories"
                                class="font-medium"
                                >{{ entry.calories }} kcal</span
                            >
                            <span
                                v-if="entry.protein_g"
                                class="text-base-content/60"
                                >P {{ entry.protein_g }}g</span
                            >
                            <span
                                v-if="entry.carbs_g"
                                class="text-base-content/60"
                                >C {{ entry.carbs_g }}g</span
                            >
                            <span
                                v-if="entry.fat_g"
                                class="text-base-content/60"
                                >F {{ entry.fat_g }}g</span
                            >
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <BottomSheet v-model="sheetOpen">
            <template v-if="selectedEntry">
                <img
                    :src="selectedEntry.image_url || '/placeholder.png'"
                    :alt="selectedEntry.name"
                    class="w-full h-48 object-cover rounded-xl mb-4"
                />

                <div class="flex items-start justify-between gap-2 mb-1">
                    <h2 class="text-xl font-semibold">{{ selectedEntry.name || 'Processing...' }}</h2>
                    <span
                        v-if="selectedEntry.category"
                        class="badge badge-ghost shrink-0"
                        >{{ selectedEntry.category }}</span
                    >
                </div>
                <p
                    v-if="selectedEntry.description"
                    class="text-base-content/60 text-sm mb-4"
                >
                    {{ selectedEntry.description }}
                </p>

                <div
                    v-if="selectedEntry.state === 'processed'"
                    class="grid grid-cols-3 gap-2 mb-4"
                >
                    <div
                        v-if="selectedEntry.calories"
                        class="bg-base-200 rounded-lg p-2 text-center"
                    >
                        <div class="text-lg font-semibold">{{ selectedEntry.calories }}</div>
                        <div class="text-xs text-base-content/50">kcal</div>
                    </div>
                    <div
                        v-if="selectedEntry.protein_g"
                        class="bg-base-200 rounded-lg p-2 text-center"
                    >
                        <div class="text-lg font-semibold">{{ selectedEntry.protein_g }}g</div>
                        <div class="text-xs text-base-content/50">protein</div>
                    </div>
                    <div
                        v-if="selectedEntry.carbs_g"
                        class="bg-base-200 rounded-lg p-2 text-center"
                    >
                        <div class="text-lg font-semibold">{{ selectedEntry.carbs_g }}g</div>
                        <div class="text-xs text-base-content/50">carbs</div>
                    </div>
                    <div
                        v-if="selectedEntry.fat_g"
                        class="bg-base-200 rounded-lg p-2 text-center"
                    >
                        <div class="text-lg font-semibold">{{ selectedEntry.fat_g }}g</div>
                        <div class="text-xs text-base-content/50">fat</div>
                    </div>
                    <div
                        v-if="selectedEntry.fiber_g"
                        class="bg-base-200 rounded-lg p-2 text-center"
                    >
                        <div class="text-lg font-semibold">{{ selectedEntry.fiber_g }}g</div>
                        <div class="text-xs text-base-content/50">fiber</div>
                    </div>
                    <div
                        v-if="selectedEntry.sugar_g"
                        class="bg-base-200 rounded-lg p-2 text-center"
                    >
                        <div class="text-lg font-semibold">{{ selectedEntry.sugar_g }}g</div>
                        <div class="text-xs text-base-content/50">sugar</div>
                    </div>
                </div>

                <div
                    v-if="selectedEntry.estimated_portion"
                    class="text-sm text-base-content/50 mb-3"
                >
                    Portion: {{ selectedEntry.estimated_portion }}
                </div>

                <div
                    v-if="selectedEntry.components?.length"
                    class="mb-4"
                >
                    <div class="text-xs font-medium text-base-content/50 uppercase tracking-wide mb-2">Components</div>
                    <div class="flex flex-wrap gap-1">
                        <span
                            v-for="(component, i) in selectedEntry.components"
                            :key="i"
                            class="badge badge-ghost text-xs"
                            >{{ component }}</span
                        >
                    </div>
                </div>

                <div
                    v-if="selectedEntry.notes"
                    class="text-sm text-base-content/50 italic"
                >
                    {{ selectedEntry.notes }}
                </div>
            </template>
        </BottomSheet>
    </div>
</template>

<script>
import BottomSheet from 'components/bottom-sheet.vue';

const COMPACT_THRESHOLD = 5;

export default {
    name: 'Entries',
    components: { BottomSheet },
    props: {
        entries: {
            type: Array,
            required: true,
        },
    },
    data: () => ({
        selectedEntry: null,
    }),
    computed: {
        compact() {
            return this.entries.length >= COMPACT_THRESHOLD;
        },
        sheetOpen: {
            get() {
                return this.selectedEntry !== null;
            },
            set(val) {
                if (!val) this.selectedEntry = null;
            },
        },
    },
};
</script>
